import React, { useEffect, useMemo, useState } from "react";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "../ui/breadcrumb";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../ui/card";
import { ObjectListFilters } from "./object-list-filters";
import { BatchAction, Filter, ObjectLayoutProps, ObjectListColumn } from "./types";

import { cn } from "@/lib/utils";
import { PaginatedResponse } from "@/types/common";
import { ListFilter, Loader2, RotateCcw } from "lucide-react";
import { Await, Location, useLocation, useNavigate, useNavigation, useSearchParams } from "react-router-dom";
import { Button } from "../ui/button";
import { ObjectBatchActions } from "./object-list-batch-actions";
import { ObjectListPagination } from "./object-list-pagination";
import { ObjectQuickFilter } from "./object-list-quick-filter";
import { ObjectListSearch } from "./object-list-search";
import { ObjectListTable } from "./object-list-table";

interface ObjectListLayoutProps<T> extends ObjectLayoutProps {
    paginated_response: PaginatedResponse<T>;
    columns: ObjectListColumn<T>[];
    new_dialog?: React.ReactNode;
    onRowNavigateTo?: (instance: T, currentLocation: Location) => string | Map<string, string>;
    search_param?: string;
    search_component?: React.ReactNode;
    quickFilters?: Filter[];
    filters?: Filter[];
    pre_card?: React.ReactNode[];
    links?: React.ReactNode[];
    actions?: React.ReactNode[];
    allowAutoRedirect?: boolean;
    allowBatchSelection?: boolean;
    batchActions?: BatchAction[];
    itemToId?: (item: T) => string;
}

export function ObjectListLayout<T>(props: ObjectListLayoutProps<T>) {
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();
    const [searchParams, setSearchParams] = useSearchParams();

    const hasActiveQuickFilters =
        props.quickFilters?.some(filter => searchParams.has(filter.field_name)) ||
        Boolean(props.search_param && searchParams.has(props.search_param));

    const clearQuickFilters = () => {
        const nextParams = new URLSearchParams(searchParams);
        props.quickFilters?.forEach(filter => nextParams.delete(filter.field_name));
        if (props.search_param) {
            nextParams.delete(props.search_param);
        }
        nextParams.set("page", "1");
        setSearchParams(nextParams);
    };

    const document_title = useMemo(() => {
        if (props.page_title) {
            return `${props.page_title} | Walter CRM`;
        }
        return "Walter CRM";
    }, [props.page_title]);

    // Set document title
    useEffect(() => {
        document.title = document_title;
    }, [document_title]);

    const [selectedItems, setSelectedItems] = useState<T[]>([]);
    const [selectAll, setSelectAll] = useState<boolean>(false);

    const [filtersOpen, setFilterOpen] = useState(false);
    const [activeFilters, setActiveFilters] = useState<Filter[]>([]);

    const onPageLoad = async () => {
        const paginated_response = await props.paginated_response;
        if (props.allowAutoRedirect && searchParams.has("autoRedirect") && paginated_response.results.length == 1) {
            const navigateTo = props.onRowNavigateTo?.(paginated_response.results[0], location);
            if (typeof navigateTo === "string") {
                console.log("navigateTo", navigateTo);
                navigate(navigateTo, { replace: true });
            }
        } else if (props.allowBatchSelection) {
            setSelectedItems([]);
            setSelectAll(false);
        }
    };

    useEffect(() => {
        onPageLoad();
    }, [props.paginated_response.results]);

    return (
        <>
            <div className="mb-5 flex items-center justify-between">
                <div>
                    {props.breadcrumbs && (
                        <Breadcrumb>
                            <BreadcrumbList>
                                {props.breadcrumbs.map((item, index) =>
                                    index < props.breadcrumbs!.length - 1 ? (
                                        <React.Fragment key={`${item.name}_${index}`}>
                                            <BreadcrumbItem>
                                                <BreadcrumbLink href={item.href ?? ""}>{item.name}</BreadcrumbLink>
                                            </BreadcrumbItem>
                                            <BreadcrumbSeparator />
                                        </React.Fragment>
                                    ) : (
                                        <BreadcrumbPage key={`${index}-${item.name}`}>{item.name}</BreadcrumbPage>
                                    )
                                )}
                            </BreadcrumbList>
                        </Breadcrumb>
                    )}
                </div>
            </div>
            <div className="mb-5 flex items-center justify-between">
                <div className="flex space-x-2">
                    {props.search_component ? props.search_component : props.search_param && <ObjectListSearch search_param={props.search_param} />}
                    {props.quickFilters && props.quickFilters.filter(qf => !qf.hidden).map((qf, i) => <ObjectQuickFilter key={`${qf.name}__${i}`} filter={qf} />)}
                    {hasActiveQuickFilters && (
                        <Button variant="outline" size="sm" className="h-9 gap-1 bg-white" onClick={clearQuickFilters}>
                            <RotateCcw className="h-3.5 w-3.5" />
                            <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">Réinitialiser</span>
                        </Button>
                    )}
                </div>
                <div className="flex items-center space-x-2">
                    {props.links && props.links.map((action, index) => <React.Fragment key={`action_${index}`}>{action}</React.Fragment>)}
                    {props.filters && (
                        <Button variant="outline" size="sm" className="h-7 gap-1" onClick={() => setFilterOpen(v => !v)}>
                            <ListFilter className="h-3.5 w-3.5" />
                            <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">Filtres avancés ({activeFilters.length})</span>
                        </Button>
                    )}
                </div>
            </div>
            <div className="relative flex items-stretch">
                <div className={cn(filtersOpen ? "w-[calc(100%-475px)]" : "w-full", "space-y-5 transition-all duration-300")}>
                    {props.pre_card && props.pre_card.map((action, index) => <React.Fragment key={`pre_card_${index}`}>{action}</React.Fragment>)}
                    <Card className={props.className}>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className={cn(props.allowBatchSelection ? "flex h-[32px] items-center" : "")}>
                                        <span>{props.page_title}</span>
                                        {props.allowBatchSelection && selectedItems.length > 0 && (
                                            <>
                                                <span className="ml-2 space-x-2 text-sm font-normal">
                                                    {selectAll ? (
                                                        <>
                                                            {props.paginated_response.count} élément{props.paginated_response.count > 1 && "s"} sélectionné{props.paginated_response.count > 1 && "s"}
                                                        </>
                                                    ) : (
                                                        <>
                                                            {selectedItems.length} élément{selectedItems.length > 1 && "s"} sélectionné{selectedItems.length > 1 && "s"}
                                                        </>
                                                    )}
                                                </span>
                                                {!selectAll && selectedItems.length == props.paginated_response.results.length && (
                                                    <Button variant="ghost" size="sm" onClick={() => setSelectAll(true)}>
                                                        Sélectionner {props.paginated_response.count} éléments
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </CardTitle>
                                    {props.page_subtitle ?? <CardDescription>{props.page_subtitle}</CardDescription>}
                                </div>
                                <div className="space-x-2">
                                    {props.allowBatchSelection && props.batchActions && props.itemToId && (selectedItems.length > 0 || selectAll) ? (
                                        <ObjectBatchActions
                                            actions={props.batchActions}
                                            selectedItemIds={selectedItems.map(item => props.itemToId?.(item) ?? "")}
                                            selectAll={selectAll}
                                            selectAllCount={props.paginated_response.count}
                                        />
                                    ) : (
                                        <>
                                            {props.actions && props.actions.map((action, index) => <React.Fragment key={`secondary_action_${index}`}>{action}</React.Fragment>)}
                                            {props.new_dialog}
                                        </>
                                    )}
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <ObjectListTable
                                paginated_response={props.paginated_response}
                                columns={props.columns}
                                onRowNavigateTo={props.onRowNavigateTo}
                                allowAutoRedirect={props.allowAutoRedirect}
                                allowBatchSelection={props.allowBatchSelection}
                                selectedItems={selectedItems}
                                setSelectedItems={setSelectedItems}
                                setSelectAll={setSelectAll}
                                selectAll={selectAll}
                            />
                        </CardContent>
                        <CardFooter>
                            <React.Suspense>
                                <Await resolve={props.paginated_response} errorElement={<p>An error occurred</p>}>
                                    {(paginated_response: PaginatedResponse<T>) => <ObjectListPagination paginated_response={paginated_response} />}
                                </Await>
                            </React.Suspense>
                        </CardFooter>
                    </Card>
                </div>

                {props.filters && (
                    <Card className={cn(filtersOpen ? "right-0 ml-[25px] opacity-100" : "right-[-400px] opacity-0", "absolute top-0 transition-[opacity,right] duration-300", "min-h-full w-[450px]")}>
                        <CardHeader>
                            <CardTitle>
                                <span className="flex items-center space-x-2">
                                    <span>Filtres</span>
                                    {navigation.state == "loading" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                </span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ObjectListFilters filters={props.filters} activeFilters={activeFilters} setActiveFilters={setActiveFilters} />
                        </CardContent>
                    </Card>
                )}
            </div>
        </>
    );
}
